/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { PageSpecificAnalyticsDataType } from '@/types/analytics';
import { getAnalyticsRelativeComplementOf } from '@/utils/analytics/getAnalyticsRelativeComplementOf';
import { eventTypes } from '@/constants/event';
import { searchAnalyticsKeys } from '@/constants/analytics/pageSpecificRemoveAnalyticsKeys';
import root from 'window-or-global';
import { getPageName } from '@/utils/analytics/legacyUtils/getPageName';
import { analyticsDynamicPageName } from '@/utils/analytics/legacyUtils/analyticsDynamicPageName';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { getProductStrikethroughAnalyticValue } from '@/components/ProductDetail/analytics/getProductStrikethroughAnalyticValue';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import usePrevious from '@/utils/usePrevious';
import { useEffect } from 'react';
import { sessionStorage } from '@/utils/sessionStorage';
import { useFeatureFlag } from '@/features/globalConfig';
import { siteSections } from '@/utils/siteSections';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import { getSeoPageType } from '@/utils/analytics/legacyUtils/getSeoPageType';
import productDetailAnalytics from '@/components/ProductDetail/productDetailAnalytics';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useProductSkuDeals } from '@/features/product/api/getProductDeals';
import { type VideoResponseModel, type SylvaniaVideoModel } from '@/api/types/products-types';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { usePageViewCall } from '@/utils/analytics/usePageViewCall';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { useReviewStatistics } from '@/features/customReviews/api/getReviewStatistics';
import { roundValueToPrecision } from '@/components/Rating/utils';
import { keepPreviousData } from '@tanstack/react-query';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { type VehicleFit } from '@/api/types/browse-search-types';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import { getSearchPlatform, isGoogleRetailPlatform } from '@/utils/analytics/getSearchPlatform';
import { getUtagMainVId } from '@/utils/cookie';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getStoredInteractionLocation } from '@/features/ymme/utils/ymmeLocationHelper';
import { type VehicleInstallGuideVideo } from '../interface';
import { useSearchServingConfig } from '@/features/search/hooks/useSearchServingConfig';
import { getPfmLocationFromIntcmp } from '@/utils/analytics/getPfmLocationFromIntcmp';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { badges } from '@/constants/analytics/badges';
import { isBonusRewardsBadgeShown } from '@/features/shelf/components/PageTemplates/Shelf/ResultsList/utils';

type AnalyticsViewPayload = {
  categoryLevel1: string;
  categoryLevel2: string;
  categoryLevel3: string;
  categoryLevel4: string;
  pageName: string;
  siteSection: string;
  pageType: string;
  pageHierarchy: string;
  seoPageType?: string;
  seoPopularSearches?: boolean;
  seoPopularSearchesClick: boolean;
  autoCorrectedKeyword: string;
  vehicleId?: string;
  fitsVehicle?: boolean;
  prodView: string;
  skuView: string[];
  productFindingMethod?: string;
  pfmLocation?: string;
  navigationInteraction?: string;
  navigationLink?: string;
  internalCampaignInteraction?: string;
  productStrikeThroughPricing: string[];
  searchMethod: string;
  tvVideoName: string;
  videoAvailable: boolean;
  productRank?: string | number | null | undefined;
  vehicleSpecificImage: boolean;
  recordType?: string;
  productAverageRating?: string[];
  productReviewCount?: string[];
  enhancedPDPContent: boolean;
  badgesInfo?: string[];
  finderFeature?: string;
};

type PdpAnalyticsProps = {
  addingToCartStatus: string;
  sylvaniaRequestIsSuccess: boolean;
  installVideosEnabled: boolean;
  recommendationsClicked: boolean;
  analyticsExecuted: boolean;
  discountedPrice: string | null | undefined;
  vehicleId: string | undefined;
  fitsVehicle: boolean;
  shouldShowDealsButton: boolean | undefined;
  sylvaniaData: VideoResponseModel | undefined;
  partTypeHasVehicleImages: boolean;
  setAnalyticsExecuted: (value: boolean) => void;
  vehicleFit?: VehicleFit | undefined;
  vehicleFitmentLabel?: string | undefined;
  vehicleInstallationGuideVideos?: VehicleInstallGuideVideo[] | null;
  vehicleInstallGuideRequestFetch?: boolean;
  isPremiumPDPEnabled: boolean;
  isEnhancedContentPresent: boolean;
  isEnhancedPDPReady: boolean;
  isDefaultPDPFetched: boolean;
  isProductDetailsQueryFetched: boolean;
  isProductDetailsQueryFetching: boolean;
};

const { siteSectionPdp, siteSectionCatalog } = siteSections;

const getSylvaniaVideoNames = (videos: SylvaniaVideoModel[] | undefined) => {
  if (!videos) {
    return '';
  }
  const videoNames: string[] = [];
  videos.forEach((video) => {
    video.videos.forEach((singleVideo) => {
      videoNames.push(singleVideo.title);
    });
  });
  return videoNames.join('|');
};

export function usePdpAnalytics({
  recommendationsClicked,
  installVideosEnabled,
  sylvaniaRequestIsSuccess,
  addingToCartStatus,
  analyticsExecuted,
  discountedPrice,
  vehicleId,
  fitsVehicle,
  shouldShowDealsButton,
  sylvaniaData,
  partTypeHasVehicleImages,
  setAnalyticsExecuted,
  vehicleFit,
  vehicleFitmentLabel,
  vehicleInstallationGuideVideos,
  vehicleInstallGuideRequestFetch,
  isPremiumPDPEnabled,
  isEnhancedContentPresent,
  isEnhancedPDPReady,
  isDefaultPDPFetched,
  isProductDetailsQueryFetched,
  isProductDetailsQueryFetching,
}: PdpAnalyticsProps) {
  const router = useLegacyRouter();

  const isNewDealsApiEnabled = useFeatureFlag('IS_NEW_DEALS_API_ENABLED') === 'true';
  const isSuperSkusEnabled = useFeatureFlag('IS_SUPER_SKUS_ENABLED') === 'true';
  const strikeThroughPricingFlag = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const DLM_PDP_ENABLED = useFeatureFlag('DLM_PDP_ENABLED') === 'true';
  const isCustomReviewsEnabled = useFeatureFlag('CUSTOM_REVIEWS_ENABLED') === 'true';
  const isBonusRewardsBadgeEnabled = useFeatureFlag('BONUS_REWARDS_BADGE_ENABLE') === 'true';
  const locale = useLocale();
  const isUS = locale === countryCodes.us;

  const isProductVehicleImageEnabled =
    useFeatureFlag('IS_PRODUCT_VEHICLE_IMAGE_ENABLED') === 'true';

  const isUtagDefined = useAnalytics();

  const { pageSpecificData, setPageSpecificData } = usePageSpecificData();

  const productDetailsQuery = useProductDetails({ placeholderData: keepPreviousData });
  const productDetails = productDetailsQuery.data?.product;
  const productSkuId =
    productDetailsQuery.isSuccess && productDetails?.itemId ? [productDetails?.itemId] : null;
  const skuDetailsResult = useProductSkuDetails({
    skuIds: productSkuId,
  });
  const skuDetailsData = skuDetailsResult.isSuccess ? skuDetailsResult.data[0] : null;
  const prevSkuDetailsData = usePrevious(skuDetailsData);
  const previousPath = usePrevious(router.asPath);

  const { data: skuDealsData } = useProductSkuDeals({
    skuIds: productSkuId,
    enabled: isNewDealsApiEnabled,
  });

  const preferredVehicle = usePreferredVehicle();
  const experimentId = useExperimentId();
  const { data: headerData } = useHeaderData();
  const servingConfig = useSearchServingConfig();

  const callPageView = usePageViewCall();

  // Google Cloud Retail analytics fields (PDP doesn't have filter field)
  const googleRetailEnabled = isGoogleRetailPlatform(experimentId);
  const searchPlatform = googleRetailEnabled ? getSearchPlatform(experimentId) : undefined;
  const visitorId = googleRetailEnabled ? getUtagMainVId() : undefined;
  const userId = headerData?.myAccountMap?.customerID;
  const recommendationAttributionToken = googleRetailEnabled
    ? sessionStorage.getItem('googleRetailRecommendationAttributionToken') || undefined
    : undefined;

  const isRedirect = Boolean(productDetailsQuery.data?.redirectURL);
  const pdpBreadcrumb = productDetails?.breadCrumbs;

  const vehicleSpecific = productDetails?.vehicleSpecificFlag ?? false;

  const {
    productFindingMethod: productFindingMethodFromHook,
    pfmLocation: pfmLocationFromHook,
    navigationInteraction: navigationInteractionFromHook,
    navigationLink: navigationLinkFromHook,
    productRecommendationType: productRecommendationTypeFromHook,
    productRecommendationInteraction: productRecommendationInteractionFromHook,
    finderFeature: finderFeatureFromHook,
  } = pageSpecificData;

  const skuNumber = productDetails?.itemId ?? '';

  const { data: reviewStatisticsData } = useReviewStatistics({
    skuNumbers: [skuNumber],
    enabled: isCustomReviewsEnabled && Boolean(skuNumber),
  });

  const reviewStatistic = reviewStatisticsData?.[skuNumber];
  const { averageOverallRating, totalReviewCount } = reviewStatistic ?? {};

  const rating = !reviewStatistic
    ? undefined
    : averageOverallRating
      ? roundValueToPrecision(Number(averageOverallRating), 0.1)
      : '';

  const reviewCount = !reviewStatistic
    ? undefined
    : totalReviewCount
      ? Number(totalReviewCount)
      : '';

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));

  const screenSize = isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop';

  useEffect(() => {
    const storedInteractionLocation = getStoredInteractionLocation();

    if (storedInteractionLocation === 'Needs more info') {
      setAnalyticsExecuted(false);
    }
  }, [setAnalyticsExecuted, preferredVehicle]);

  useEffect(() => {
    let resultsChangesAfterRecommendationsClicked = false;

    if (recommendationsClicked) {
      resultsChangesAfterRecommendationsClicked = skuDetailsData === prevSkuDetailsData;
    }

    const acceptableReadyStatus =
      productDetailsQuery.isSuccess &&
      skuDetailsResult.isSuccess &&
      !isRedirect &&
      !resultsChangesAfterRecommendationsClicked &&
      vehicleInstallGuideRequestFetch &&
      (!installVideosEnabled || sylvaniaRequestIsSuccess) &&
      isDefaultPDPFetched &&
      isProductDetailsQueryFetched &&
      !isProductDetailsQueryFetching &&
      isEnhancedPDPReady;

    if (!analyticsExecuted && isUtagDefined && pdpBreadcrumb && acceptableReadyStatus) {
      const pdpUtagViewAffinity = sessionStorage.getItem('pdpUtagViewAffinity') === 'true';
      const productFindingMethodFromUtag =
        typeof root.utag_data?.productFindingMethod === 'string'
          ? root.utag_data?.productFindingMethod
          : '';

      const productFindingMethod =
        sessionStorage.getItem('productFindingMethod') ??
        productFindingMethodFromHook ??
        productFindingMethodFromUtag;

      const hasIntcmp = Array.isArray(router.query.intcmp)
        ? router.query.intcmp.some(Boolean)
        : Boolean(router.query.intcmp);
      const intcmpValue = Array.isArray(router.query.intcmp)
        ? (router.query.intcmp.filter(Boolean)[0] ?? '')
        : (router.query.intcmp ?? '');

      const previousPageURL = root.utag_data?.previousPageURL;
      const documentReferrer = root.document?.referrer;
      const isFromFixFinder =
        (typeof previousPageURL === 'string' && previousPageURL.includes('/lp/fix-finder')) ||
        (typeof documentReferrer === 'string' && documentReferrer.includes('/lp/fix-finder'));

      // Check if user came from a Finder feature (takes precedence over internal campaigns)
      const isFinderFeature =
        isFromFixFinder ||
        Boolean(finderFeatureFromHook) ||
        productFindingMethodFromHook === 'Finder';

      const pfmLocationFromIntcmp = getPfmLocationFromIntcmp({
        intcmp: router.query.intcmp,
        previousPageURL,
        categoryPageType: sessionStorage.getItem('categorypagetype'),
      });
      const isInternalCampaign = hasIntcmp && Boolean(pfmLocationFromIntcmp) && !isFinderFeature;
      const isSuperSkuNavigation = productFindingMethod === 'Super SKU';
      const isBuyAgainNavigation = productRecommendationTypeFromHook === 'Buy Again';

      const pfmLocationCalculated =
        isSuperSkuNavigation || isBuyAgainNavigation
          ? pfmLocationFromHook
          : productFindingMethod && pfmLocationFromHook
            ? `${productFindingMethod}:${screenSize}:${pfmLocationFromHook}`
            : undefined;

      const pfmLocation = pfmLocationFromIntcmp ?? pfmLocationCalculated;

      const isSuperSkuPage =
        isSuperSkusEnabled && !!productDetails?.choices && !!productDetails.availableOptions;
      let dynamicPageName = '';

      if (Object.keys(pdpBreadcrumb).length > 0) {
        const pageName = analyticsDynamicPageName(pdpBreadcrumb, siteSectionPdp);
        const skuId = skuDetailsData?.skuPricingAndAvailability.skuId ?? '';
        dynamicPageName = `${pageName}-${skuId}${isSuperSkuPage ? ':SUPER' : ''}`;
      }

      const breadCrumbList = pdpBreadcrumb.map((crumb) => crumb.displayName);
      const categoryLevel1 = breadCrumbList[1] || '';
      const categoryLevel2 = breadCrumbList[2] || '';
      const categoryLevel3 = breadCrumbList[3] || '';
      const categoryLevel4 = breadCrumbList[4] || '';
      const lwTestExperience = sessionStorage.getItem('lwTestExperience');
      const selectedPlatform = sessionStorage.getItem('pbs-search-engine');
      const stageName = sessionStorage.getItem('search-stage-name');
      const responseTimeMs = sessionStorage.getItem('search-query-time');
      const productStrikeThroughPricing = getProductStrikethroughAnalyticValue(
        skuDetailsData?.skuPricingAndAvailability,
        discountedPrice,
        strikeThroughPricingFlag,
      );

      const performedSearch = sessionStorage.getItem('performedSearch') === 'true';
      const productRank = isNaN(Number(sessionStorage.getItem('productRank')))
        ? sessionStorage.getItem('productRank')
        : Number(sessionStorage.getItem('productRank'));

      const isBonusRewardsBadge = isBonusRewardsBadgeShown(
        productDetails?.bonusRewardEligible,
        productDetails?.bonusRewardAmount,
        productDetails?.bonusRewardStartDate,
        productDetails?.bonusRewardEndDate,
        isBonusRewardsBadgeEnabled,
        isUS,
      );

      const analyticsViewPayload: AnalyticsViewPayload = {
        categoryLevel1,
        categoryLevel2,
        categoryLevel3,
        categoryLevel4,
        pageName: getPageName(pageTypes.pdp, dynamicPageName),
        siteSection: siteSectionCatalog,
        pageType: pageTypes.pdp,
        searchMethod: sessionStorage.getItem('searchMethod') || '',
        pageHierarchy: `Home|${
          dynamicPageName.includes('-')
            ? dynamicPageName
                .replace(/-/g, ' ')
                .replace(/:/g, '|')
                .replace(/\b([a-z])/g, (x) => x.toUpperCase())
            : dynamicPageName.replace(/\b([a-z])/g, (x) => x.toUpperCase())
        }`,
        ...(pfmLocation && { pfmLocation }),
        ...(isSuperSkuNavigation &&
          navigationInteractionFromHook && {
            navigationInteraction: navigationInteractionFromHook,
          }),
        ...(isSuperSkuNavigation &&
          navigationLinkFromHook && {
            navigationLink: navigationLinkFromHook,
          }),
        ...(productRecommendationTypeFromHook && {
          productRecommendationType: productRecommendationTypeFromHook,
          productRecommendationInteraction: productRecommendationInteractionFromHook,
        }),
        ...(lwTestExperience && { lwTestExperience }),
        seoPageType: getSeoPageType(pageTypes.pdp, window?.location.pathname),
        ...(sessionStorage.getItem('productRank') !== null &&
          sessionStorage.getItem('productRank') !== undefined && { productRank }),
        seoPopularSearches: !!document.getElementById('#popular-searches-container'),
        seoPopularSearchesClick: sessionStorage.getItem('popularSearchClicked') === 'true',
        ...productDetailAnalytics({
          productDetails,
          skuDetailsData: skuDetailsData,
          autoCorrectedKeyword: '',
          searchRedirectText:
            typeof router.query.searchText === 'string' ? router.query.searchText : '',
          hasDealBadge: shouldShowDealsButton,
          isNewDealsApiEnabled,
          skuDealsData,
          vehicleFit,
          locale,
          vehicleFitmentLabel,
        }),
        prodView: eventTypes.prodView,
        skuView: ['1'],
        ...(isFinderFeature
          ? {
              productFindingMethod: 'Finder',
              ...(finderFeatureFromHook
                ? { finderFeature: finderFeatureFromHook }
                : isFromFixFinder
                  ? { finderFeature: 'Fix Finder' }
                  : {}),
            }
          : productFindingMethod && {
              productFindingMethod: isInternalCampaign
                ? 'Internal Campaign'
                : isSuperSkuNavigation
                  ? 'Navigation'
                  : productFindingMethod,
            }),
        ...(isInternalCampaign && {
          internalCampaignInteraction: 'internalCampaignInteraction',
        }),
        ...(hasIntcmp && { campaignID: intcmpValue }),
        productStrikeThroughPricing: productStrikeThroughPricing,
        tvVideoName:
          vehicleInstallationGuideVideos && vehicleInstallationGuideVideos.length > 0
            ? vehicleInstallationGuideVideos.map((v) => v.title).join('|')
            : getSylvaniaVideoNames(sylvaniaData?.sylvaniaVideos),
        videoAvailable:
          (vehicleInstallationGuideVideos && vehicleInstallationGuideVideos.length > 0) ||
          (installVideosEnabled && !!sylvaniaData?.sylvaniaVideos?.length),
        vehicleSpecificImage:
          isProductVehicleImageEnabled && partTypeHasVehicleImages && !!preferredVehicle,
        ...(productDetails?.recordType && { recordType: productDetails.recordType }),
        productAverageRating: rating !== undefined ? [String(rating)] : undefined,
        productReviewCount: reviewCount !== undefined ? [String(reviewCount)] : undefined,
        ...(isBonusRewardsBadge && { badgesInfo: [badges.bonusRewards] }),
        ...(userId && { GCMID: userId }),
        // Google Cloud Retail fields (PDP doesn't include filter field)
        ...(googleRetailEnabled && {
          visitorId,
          searchPlatform,
          servingConfig,
          ...(recommendationAttributionToken && {
            attributionToken: recommendationAttributionToken,
          }),
        }),
        enhancedPDPContent: isEnhancedContentPresent || isPremiumPDPEnabled,
        ...(selectedPlatform && { selected_platform: selectedPlatform }),
        ...(stageName && { stage_name: stageName }),
        ...(responseTimeMs && { response_time_ms: Number(responseTimeMs) }),
      };

      const enhancedPDPContentValue = isEnhancedContentPresent || isPremiumPDPEnabled;
      sessionStorage.setItem('enhancedPDPContent', enhancedPDPContentValue ? 'true' : 'false');

      if (pdpUtagViewAffinity) {
        const pageSpecificAnalyticsData: PageSpecificAnalyticsDataType = {
          ...analyticsViewPayload,
          productFindingMethod: 'Product Recommendation',
          productRecommendationInteraction: 'productRecommendationInteraction',
          pfmLocation: 'Affinity:catalog_product_detail',
          productRecommendationType: 'Complete The Job',
        };

        callPageView(
          pageSpecificAnalyticsData,
          getAnalyticsRelativeComplementOf(pageSpecificAnalyticsData, undefined),
        );
        sessionStorage.setItem('pdpUtagLinkAffinity', 'true');
        sessionStorage.removeItem('pdpUtagViewAffinity');
      } else {
        const analyticsDataToRemove =
          !performedSearch && typeof router.query.searchText === 'string'
            ? [...searchAnalyticsKeys, 'eventType']
            : searchAnalyticsKeys;

        callPageView(
          analyticsViewPayload,
          getAnalyticsRelativeComplementOf(analyticsViewPayload, analyticsDataToRemove),
        );
      }

      setPageSpecificData({
        productFindingMethod: '',
        pfmLocation: '',
        navigationInteraction: '',
        navigationLink: '',
        productRecommendationType: '',
        productRecommendationInteraction: '',
        internalCampaignInteraction: '',
        finderFeature: '',
      });
      sessionStorage.setItem('popularSearchClicked', 'false');
      sessionStorage.removeItem('searchMethod');
      sessionStorage.removeItem('performedSearch');
      sessionStorage.removeItem('lwTestExperience');
      sessionStorage.removeItem('productRank');
      sessionStorage.removeItem('pbs-search-engine');
      sessionStorage.removeItem('search-stage-name');
      sessionStorage.removeItem('search-query-time');
      setAnalyticsExecuted(true);
    } else if (analyticsExecuted && previousPath === router.asPath) {
      sessionStorage.removeItem('productFindingMethod');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addingToCartStatus,
    isUtagDefined,
    pdpBreadcrumb,
    skuDetailsResult.isSuccess,
    productDetailsQuery.isSuccess,
    isRedirect,
    analyticsExecuted,
    productDetails,
    skuDetailsData,
    vehicleId,
    fitsVehicle,
    vehicleSpecific,
    router.query.searchText,
    DLM_PDP_ENABLED,
    recommendationsClicked,
    rating,
    reviewCount,
    reviewStatistic,
    isCustomReviewsEnabled,
    isProductDetailsQueryFetched,
    isProductDetailsQueryFetching,
    isDefaultPDPFetched,
    isPremiumPDPEnabled,
    isEnhancedContentPresent,
    isEnhancedPDPReady,
    googleRetailEnabled,
    searchPlatform,
    servingConfig,
    isBonusRewardsBadgeEnabled,
    isUS,
  ]);
}
